import { useState, useEffect, useRef } from "react";
import { dismissModal } from "@/utils/modalClose";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon,
  IonCard, IonCardHeader, IonCardTitle, IonChip, IonText, IonSpinner, IonNote,
  IonList, IonItem, IonLabel, IonRadioGroup, IonRadio,
} from "@ionic/react";
import { closeOutline, checkmarkCircle, cardOutline } from "ionicons/icons";
import { useStripe, useElements, CardNumberElement, PaymentRequestButtonElement } from "@stripe/react-stripe-js";
import { useCardForm, StripeMark, CardBrandIcon, cardName, cardNote } from "./CardForm";
import { userHeaders } from "@/utils/userAuth";
import { emitAppLive } from "@/utils/appLive";
import { appThemeDark } from "@/utils/appTheme";
import { Capacitor } from "@capacitor/core";
import NativePaywall from "./NativePaywall";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// The Billing page's Activate flow: a checkout in the same portalled slide-up
// modal as the document preview / Secure Checkout (600px wide, up to 90vh on
// desktop; full screen on phones) instead of Stripe's hosted page. The
// selected plan sits at the top, then the payment form card: Apple Pay /
// Google Pay on devices with a wallet, the card form in Stripe Elements (card
// details never touch our inputs), the consent text, and Pay now. Mount it
// fresh per open (render it conditionally).
//
// props: plan { id, name, monthly, annual }, interval "month" | "year",
//        onClose, onActivated (after the plan is live)
// businessId, when given, makes this checkout a further business's own
// subscription rather than the account's: the server binds the Stripe
// subscription to that business and the account plan is untouched.
// wallet { amount }, in place of a plan, makes this the Wallet Balance
// card's checkout: the same modal, "Pay as you go" at the chosen amount, a
// one-off payment intent that /api/wallet/confirm credits to the balance
// (onActivated then fires once it is loaded).
// The native app sells nothing itself (the Spotify model): the link-out
// sheet replaces the Stripe checkout there. Gated here, above the Stripe
// component, so its hooks never run short.
export default function SubscriptionCheckoutModal(props) {
  if (Capacitor.isNativePlatform?.()) return <NativePaywall mode={props.wallet ? "all" : "plans"} onClose={props.onClose} />;
  return <StripeSubscriptionCheckout {...props} />;
}

function StripeSubscriptionCheckout({ plan, interval, wallet, onClose: onCloseNow, onActivated, businessId }) {
  const topUp = !!wallet; // loading the account balance rather than starting a plan
  const onClose = (e) => dismissModal(onCloseNow, e); // animated exit, then the real close

  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const isMobile = window.innerWidth < 768;
  const [error, setError] = useState("");
  const [stage, setStage] = useState("form"); // form | paying | done
  const [intent, setIntent] = useState(null); // { subscriptionId, clientSecret }, kept across a declined retry
  const [referral, setReferral] = useState(null); // { amountDue } when the account's first cycle carries the referral discount
  const [paymentRequest, setPaymentRequest] = useState(null); // Apple Pay / Google Pay, when the device has one
  // Saved cards (Billing → Payment methods), listed like the Payment methods
  // modal with the default preselected; "new" is the card form below
  const [savedCards, setSavedCards] = useState([]);
  const [defaultId, setDefaultId] = useState("");
  const [choice, setChoice] = useState("new");
  const usingSaved = choice !== "new" && savedCards.some((c) => c.id === choice);
  const latest = useRef({}); // the wallet handler reads the latest state without re-creating the request
  const busy = stage === "paying";
  const { fields, firstError, billingAddress } = useCardForm({ disabled: busy });
  const yearly = interval === "year";
  const amount = topUp ? Number(wallet.amount || 0) : (yearly ? plan.annual : plan.monthly);
  const title = topUp ? "Pay as you go" : plan.name;
  const priceLabel = topUp ? money(amount) : `${money(amount)}/${yearly ? "yr" : "mo"}`;
  // Apple Pay / Google Pay wear white on the dark theme, black on the light
  const walletButtonTheme = appThemeDark() ? "light" : "dark";
  const userInfo = (() => { try { return JSON.parse(localStorage.getItem("userInfo") || "null") || {}; } catch { return {}; } })();

  const post = async (path, body) => {
    const res = await fetch(`${BACKEND_URL}${path}`, { method: "POST", headers: userHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.detail || data.error || "Something went wrong");
    return data;
  };

  // One subscription (or top-up intent) per checkout: a declined card retries
  // the same intent. A saved card is set on the subscription server-side, and
  // a top-up intent goes on the customer that card is on, so switching
  // between a saved card and a new one starts a fresh intent.
  const ensureIntent = async (pmId = null) => {
    const cached = latest.current.intent;
    if (cached && (cached.paymentMethodId || null) === pmId) return cached;
    const d = topUp
      ? await post("/api/wallet/payment-intent", { amount, ...(pmId ? { paymentMethodId: pmId } : {}) })
      : await post("/api/stripe/create-subscription", { tier: plan.id, interval, ...(pmId ? { paymentMethodId: pmId } : {}), ...(businessId ? { businessId } : {}) });
    if (d.referralDiscount) setReferral({ amountDue: d.amountDue });
    const current = { subscriptionId: d.subscriptionId, paymentIntentId: d.paymentIntentId, clientSecret: d.clientSecret, paymentMethodId: pmId };
    setIntent(current);
    latest.current.intent = current;
    return current;
  };
  latest.current.intent = intent;

  // The card is charged, but Stripe can report the subscription as
  // "incomplete" (or the intent as not yet succeeded) for a moment before it
  // settles - retry over a few seconds instead of failing a paid checkout,
  // then let every open page know (the Billing page listens and refreshes).
  // A top-up settles through /api/wallet/confirm, which credits the balance.
  const activate = async ({ subscriptionId, paymentIntentId }) => {
    let lastErr = null;
    for (let i = 0; i < 6; i += 1) {
      try {
        if (topUp) await post("/api/wallet/confirm", { paymentIntentId });
        else await post("/api/stripe/subscription-activated", { subscriptionId });
        emitAppLive({ reason: topUp ? "wallet" : "subscription" });
        return;
      } catch (err) {
        lastErr = err;
        if (!/not completed yet/i.test(err.message || "")) break;
        await new Promise((r) => setTimeout(r, 1500));
      }
    }
    throw lastErr || new Error(topUp ? "Could not load the balance. Please refresh Billing." : "Could not activate the plan. Please refresh Billing.");
  };

  const pay = async () => {
    setError("");
    if (!stripe || (!usingSaved && !elements)) { setError("The payment form is still loading. Please try again."); return; }
    if (!usingSaved) {
      const err = firstError();
      if (err) { setError(err); return; }
    }
    setStage("paying");
    try {
      const current = await ensureIntent(usingSaved ? choice : null);
      const result = await stripe.confirmCardPayment(current.clientSecret, usingSaved ? { payment_method: choice } : {
        payment_method: {
          card: elements.getElement(CardNumberElement),
          billing_details: { name: userInfo.name || undefined, email: userInfo.email || undefined, address: billingAddress() },
        },
      });
      if (result.error) throw new Error(result.error.message || "Your card was declined. Please try again.");
      if (result.paymentIntent?.status !== "succeeded") throw new Error("Payment did not complete. Please try again.");
      await activate(current);
      setStage("done");
    } catch (err) {
      setError(err.message || "Payment failed. Please try again.");
      setStage("form");
    }
  };

  // Apple Pay / Google Pay: the wallet hands us a payment method, which pays
  // the first invoice and is kept on the subscription for renewals.
  latest.current.payWithWallet = async (ev) => {
    setError(""); setStage("paying");
    try {
      const current = await ensureIntent();
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(current.clientSecret, { payment_method: ev.paymentMethod.id }, { handleActions: false });
      if (confirmError) { ev.complete("fail"); throw new Error(confirmError.message || "Payment failed. Please try again."); }
      ev.complete("success");
      let pi = paymentIntent;
      if (pi && pi.status === "requires_action") {
        const followUp = await stripe.confirmCardPayment(current.clientSecret);
        if (followUp.error) throw new Error(followUp.error.message || "Payment authentication failed.");
        pi = followUp.paymentIntent;
      }
      if (!pi || pi.status !== "succeeded") throw new Error("Payment did not complete. Please try again.");
      await activate(current);
      setStage("done");
    } catch (err) {
      setError(err.message || "Payment failed. Please try again.");
      setStage("form");
    }
  };
  useEffect(() => {
    if (!stripe) return undefined;
    let pr;
    try {
      pr = stripe.paymentRequest({ country: "US", currency: "usd", total: { label: topUp ? "MintSlip account balance" : `MintSlip ${plan.name} plan`, amount: Math.round(Number(amount || 0) * 100) }, requestPayerName: true, requestPayerEmail: true });
    } catch { return undefined; }
    let cancelled = false;
    pr.canMakePayment().then((ok) => { if (!cancelled && ok) setPaymentRequest(pr); }).catch(() => {});
    pr.on("paymentmethod", async (ev) => { await latest.current.payWithWallet(ev); });
    return () => { cancelled = true; };
  }, [stripe]); // eslint-disable-line

  // The account's saved cards, the default card preselected
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/stripe/payment-methods`, { headers: userHeaders() });
        const d = await res.json().catch(() => ({}));
        if (!on || !res.ok || !d.success) return;
        const cards = d.cards || [];
        setSavedCards(cards);
        setDefaultId(d.defaultId || "");
        if (cards.length) setChoice(d.defaultId || cards[0].id);
      } catch { /* the card form stays */ }
    })();
    return () => { on = false; };
  }, []);

  return createPortal(
    <div className="modal-backdrop checkout-modal" style={{ position: "fixed", inset: 0, zIndex: 10010, background: isMobile ? "var(--ion-background-color, #f2f2f7)" : "rgba(0,0,0,0.55)", display: "flex", alignItems: isMobile ? "stretch" : "center", justifyContent: isMobile ? "stretch" : "center" }}>
      <div className="modal-slide-up" style={{ background: "var(--ion-background-color, #f2f2f7)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: isMobile ? "100%" : 600, height: isMobile ? "100%" : "auto", maxHeight: isMobile ? "100%" : "90vh", overflow: "hidden" }}>
        <IonHeader>
          <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
            <IonButtons slot="start">
              <IonButton fill="clear" shape="round" onClick={onClose} disabled={busy} aria-label="Close">
                <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1.8em", color: "var(--ion-text-color)" }}>
                  <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                </span>
              </IonButton>
            </IonButtons>
            <IonTitle style={{ fontWeight: 700 }}>Checkout</IonTitle>
          </IonToolbar>
        </IonHeader>

        <div className="checkout-scroll" style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
          {stage === "done" ? (
            <div className="checkout-done">
              <IonIcon icon={checkmarkCircle} color="success" />
              <div className="checkout-done-title">{topUp ? "Your account has been loaded" : `Your ${plan.name} plan is active`}</div>
              <p>{topUp ? `${money(amount)} has been added to your balance. Documents spend their exact price from it.` : "Thanks for subscribing. Your downloads are ready to use."}</p>
              <IonButton color="success" onClick={onActivated}>Done</IonButton>
            </div>
          ) : (
            <div className="app-billing checkout-body">
              <IonCard className="plan-card checkout-plan">
                <IonChip color="success" className="header-chip bold">Selected</IonChip>
                <IonCardHeader>
                  <IonCardTitle>
                    <IonText>{title}</IonText>
                    <IonText className="checkout-price">{priceLabel}</IonText>
                  </IonCardTitle>
                </IonCardHeader>
                <div className="checkout-plan-text">
                  <p>{`To ${topUp ? "load your account" : "activate this plan"}, ${savedCards.length > 0 ? "choose one of your cards below or enter a new one, then click 'Pay now'" : "please input your card details below and click 'Pay now'"}`}</p>
                  {referral && !topUp && (
                    <p className="checkout-note" style={{ color: "var(--ion-color-success)", fontWeight: 600 }}>
                      Referral discount: 10% off your first billing cycle, so today's charge is {money(referral.amountDue)}.
                    </p>
                  )}
                  <p className="checkout-note">{topUp
                    ? `NOTE: Your card is charged ${priceLabel} once, today. Your balance never expires.`
                    : `NOTE: Your card is charged ${priceLabel} today and again at each renewal until you cancel. You can cancel anytime from Billing.`}</p>
                </div>
              </IonCard>

              {/* the payment form is its own card: the Stripe mark + "Payment Form" as its header row */}
              <IonCard className="checkout-form-card">
                <div className="checkout-form-head">
                  <StripeMark />
                  <span>Payment Form</span>
                </div>
                {savedCards.length > 0 && (
                  /* the saved cards, listed as in the Payment methods modal; the last row is the card form */
                  <IonRadioGroup value={choice} onIonChange={(e) => setChoice(e.detail.value)}>
                    <IonList lines="full" className="checkout-cards">
                      {savedCards.map((c) => (
                        <IonItem key={c.id} className="pm-row" disabled={busy}>
                          <CardBrandIcon slot="start" brand={c.brand} wallet={c.wallet} />
                          <IonLabel>
                            {cardName(c)}
                            <p>{cardNote(c, c.id === defaultId)}</p>
                          </IonLabel>
                          <IonRadio slot="end" value={c.id} aria-label={`Pay with ${cardName(c)}`} />
                        </IonItem>
                      ))}
                      <IonItem className="pm-row checkout-newcard" lines="none" disabled={busy}>
                        <IonIcon slot="start" icon={cardOutline} className="checkout-newcard-icon" />
                        <IonLabel>
                          Use a new card
                          <p>Enter the card details below</p>
                        </IonLabel>
                        <IonRadio slot="end" value="new" aria-label="Pay with a new card" />
                      </IonItem>
                    </IonList>
                  </IonRadioGroup>
                )}
                <div className="checkout-form">
                  {!usingSaved && paymentRequest && (
                    <>
                      <PaymentRequestButtonElement options={{ paymentRequest, style: { paymentRequestButton: { theme: walletButtonTheme, height: "44px" } } }} />
                      <div className="pm-or">
                        <span />
                        <em>or pay with card</em>
                        <span />
                      </div>
                    </>
                  )}
                  {!usingSaved && fields}

                  {error && <IonNote color="danger" className="checkout-error">{error}</IonNote>}

                  <p className="checkout-legal">By providing your card information, you allow MintSlip to charge your card for {topUp ? "this payment" : "future payments"} in accordance with their terms.</p>
                  <p className="checkout-legal">By clicking "Pay now" below you agree to MintSlip's <span className="checkout-link" onClick={() => { onClose(); navigate("/app/terms"); }}>Terms of Service</span></p>

                  <IonButton expand="block" color="success" className="checkout-pay" onClick={pay} disabled={busy}>
                    {busy ? <IonSpinner name="crescent" /> : "Pay now"}
                  </IonButton>
                </div>
              </IonCard>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.querySelector("ion-app") || document.body
  );
}
